import React, { useMemo, useState } from "react";
import { useHSE } from "../lib/store";
import { apiFetch } from "../lib/api";
import { KPI_ROWS, MONTHS } from "../lib/sampleData";
import { Search, Save, RotateCcw, Download, FileSpreadsheet, Lock, Unlock } from "lucide-react";
import { Button } from "../components/ui/button";
import { exportCSV, exportXLSX } from "../lib/exportUtils";
import { toast } from "sonner";
import { calculateTRIR, calculateLTIFR, calculateSeverityRate, calculateClosurePercent } from "../lib/hseCalc";
import { STATUS_BADGES } from "../components/RegisterPage";

export default function MonthlyKPI() {
  const { data, setData, filters, setFilters, resetSample, siteNames } = useHSE();

  const safeSiteNames = siteNames?.length ? siteNames : ["TEST"];
  const [search, setSearch] = useState("");
  const [site, setSite] = useState(safeSiteNames[0]);
  const [draft, setDraft] = useState(data?.monthlyKPI || {});

  const activeSite = site || safeSiteNames[0] || "TEST";
  const ZERO_12 = Array(12).fill(0);

  const getSeries = (siteKey, metricKey) => {
    const series = draft?.[siteKey]?.[metricKey];
    return Array.isArray(series) ? series : ZERO_12;
  };

  const ensureSiteKPI = (siteKey, existingSiteData = {}) => {
    const siteData = { ...existingSiteData };

    KPI_ROWS.forEach((row) => {
      if (!row.computed && !Array.isArray(siteData[row.key])) {
        siteData[row.key] = [...ZERO_12];
      }
    });

    return siteData;
  };

  const visibleRows = useMemo(() => {
    return KPI_ROWS.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()));
  }, [search]);

  const getVal = (row, idx) => {
    if (row.computed) {
      const fac = Number(getSeries(activeSite, "fac")[idx]) || 0;
      const mtc = Number(getSeries(activeSite, "mtc")[idx]) || 0;
      const rwc = Number(getSeries(activeSite, "rwc")[idx]) || 0;
      const lti = Number(getSeries(activeSite, "lti")[idx]) || 0;
      const wd = Number(getSeries(activeSite, "workdaysLost")[idx]) || 0;
      const mh = Number(getSeries(activeSite, "manhours")[idx]) || 0;

      if (row.key === "trir") return calculateTRIR({ mtc, rwc, lti, manhours: mh });
      if (row.key === "ltifRate") return calculateLTIFR({ lti, manhours: mh });
      if (row.key === "ltisr") return calculateSeverityRate({ lostDays: wd, manhours: mh });
    }

    return Number(getSeries(activeSite, row.key)[idx]) || 0;
  };

  const ytd = (row) => {
    if (row.key === "manpower" || row.key === "manpowerPeak" || row.key === "peakManpower") {
      let maxVal = 0;
      for (let i = 0; i < 12; i++) {
        const val = Number(getVal(row, i)) || 0;
        if (val > maxVal) maxVal = val;
      }
      return maxVal;
    }
    let s = 0;
    for (let i = 0; i < 12; i++) s += Number(getVal(row, i)) || 0;
    return row.computed ? +(s / 12).toFixed(2) : s;
  };

  const consolidate = (row) => {
    let s = 0;
    safeSiteNames.forEach((st) => {
      for (let i = 0; i < 12; i++) {
        if (row.computed) {
          const fac = Number(getSeries(st, "fac")[i]) || 0;
          const mtc = Number(getSeries(st, "mtc")[i]) || 0;
          const rwc = Number(getSeries(st, "rwc")[i]) || 0;
          const lti = Number(getSeries(st, "lti")[i]) || 0;
          const wd = Number(getSeries(st, "workdaysLost")[i]) || 0;
          const mh = Number(getSeries(st, "manhours")[i]) || 0;
          if (row.key === "trir") s += mh ? ((mtc + rwc + lti) * 200000) / mh : 0;
          if (row.key === "ltifRate") s += mh ? (lti * 1_000_000) / mh : 0;
          if (row.key === "ltisr") s += mh ? (wd * 200_000) / mh : 0;
        } else {
          s += Number(getSeries(st, row.key)[i]) || 0;
        }
      }
    });
    const divisor = Math.max(1, 12 * safeSiteNames.length);
    return row.computed ? +(s / divisor).toFixed(2) : Math.round(s * 100) / 100;
  };

  const update = (rowKey, idx, val) => {
    if (KPI_ROWS.find((r) => r.key === rowKey)?.computed) return;

    const now = new Date();
    const curYear = now.getFullYear();
    const curMonth = now.getMonth();
    const selYear = Number(filters?.year) || curYear;
    if (selYear > curYear || (selYear === curYear && idx > curMonth)) {
      toast.error("Future monthly KPI periods cannot be created or updated.");
      return;
    }

    const num = val === "" ? 0 : Number(val);

    setDraft((d) => {
      const currentSiteData = ensureSiteKPI(activeSite, d?.[activeSite]);
      const currentSeries = Array.isArray(currentSiteData[rowKey])
        ? currentSiteData[rowKey]
        : [...ZERO_12];

      return {
        ...d,
        [activeSite]: {
          ...currentSiteData,
          [rowKey]: currentSeries.map((v, i) => (i === idx ? num : v)),
        },
      };
    });
  };

  const saveAll = async () => {
    try {
      const year = Number(filters?.year) || new Date().getFullYear();
      const res = await apiFetch("/monthly-kpi", {
        method: "POST",
        body: {
          siteId: activeSite,
          year,
          month: new Date().getMonth() + 1,
          manhours: Number(getSeries(activeSite, "manhours")[0]) || 0,
        },
      });
      setData({ ...data, monthlyKPI: draft });
      toast.success("Monthly KPI saved authoritatively");
    } catch (err) {
      if (err.code === "RECORD_LOCKED" || err.code === "FUTURE_RECORD_FORBIDDEN") {
        toast.error(err.message || err.code);
        return;
      }
      setData({ ...data, monthlyKPI: draft });
      toast.success("Monthly KPI saved");
    }
  };

  const reset = () => {
    if (!confirm("Reset all data to sample? This will erase your edits.")) return;
    resetSample();
    setTimeout(() => window.location.reload(), 100);
  };

  const exportRows = () =>
    visibleRows.map((row) => {
      const o = { KPI: row.name };
      MONTHS.forEach((m, i) => (o[m] = getVal(row, i)));
      o.YTD = ytd(row);
      o.Consolidate = consolidate(row);
      return o;
    });

  const cellColor = (row, val) => {
    const num = Number(val) || 0;
    const danger = ["lti", "incidents", "ltifRate", "ltisr", "ncrOpen", "sorOpen", "fines", "violations", "workdaysLost"];
    const positive = ["training", "inducted", "tbt", "obsClosed", "drills", "awards", "wasteRec", "inspections"];

    if (danger.includes(row.key) && num > 0) return num > 3 ? "#FEE2E2" : "#FEF3C7";
    if (positive.includes(row.key) && num > 0) return "#DCFCE7";
    return "transparent";
  };

  const monthlyStatus = data.monthlyKPIStatus || {};
  const setMonthStatus = (mYear, newStatus) => {
    setData({ ...data, monthlyKPIStatus: { ...monthlyStatus, [mYear]: newStatus } });
    toast.success(`${mYear}: ${newStatus}`);
  };

  return (
    <div className="space-y-4" data-testid="monthly-kpi-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-[#0E2336]">Monthly KPI Data</h1>
          <div className="text-sm text-slate-500">
            Editable matrix · Site: <b>{activeSite}</b> · Year {filters.year} · Corporate KPI submission workflow
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            data-testid="kpi-site-select"
            value={activeSite}
            onChange={(e) => setSite(e.target.value)}
            className="border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white max-w-[160px]"
          >
            {safeSiteNames.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>

          <select
            data-testid="kpi-year-select"
            value={filters.year}
            onChange={(e) => setFilters({ ...filters, year: e.target.value })}
            className="border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white"
          >
            {["2023", "2024", "2025", "2026"].map((y) => (
              <option key={y}>{y}</option>
            ))}
          </select>

          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2.5 py-1.5">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              data-testid="kpi-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search KPI…"
              className="text-xs bg-transparent focus:outline-none w-40"
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={() => exportCSV(exportRows(), "monthly-kpi.csv")}
          >
            <Download className="w-3.5 h-3.5" /> CSV
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={() => exportXLSX(exportRows(), "monthly-kpi.xlsx", "Monthly KPI")}
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
          </Button>

          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={reset}>
            <RotateCcw className="w-3.5 h-3.5" /> Reset Sample
          </Button>

          <Button
            data-testid="kpi-save-btn"
            onClick={saveAll}
            size="sm"
            className="h-8 text-xs gap-1.5 font-semibold"
            style={{ background: "#54A57B", color: "white" }}
          >
            <Save className="w-3.5 h-3.5" /> Save Changes
          </Button>
        </div>
      </div>

      {/* Monthly approval status ribbon */}
      <div className="command-card p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Monthly Approval Status</div>
          <div className="text-[10px] text-slate-400 font-mono">Draft → Submitted → Approved → Locked</div>
        </div>
        <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
          {MONTHS.map((m) => {
            const key = `${m}-${filters.year}`;
            const st = monthlyStatus[key] || "Draft";
            const badge = STATUS_BADGES[st] || { bg: "#F1F5F9", color: "#334155" };
            const next = st === "Draft" ? "Submitted" : st === "Submitted" ? "Approved" : st === "Approved" ? "Locked" : "Draft";
            return (
              <button
                key={m}
                type="button"
                data-testid={`kpi-approval-${m}`}
                onClick={() => setMonthStatus(key, next)}
                title={`Click to move to: ${next}`}
                className="rounded-md border border-slate-200 px-2 py-2 hover:border-[#18B84F] transition text-left"
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{m}</div>
                <div className="cmd-pill mt-1" style={{ background: badge.bg, color: badge.color }}>
                  {st === "Locked" ? <Lock className="w-3 h-3 mr-1 inline" /> : <Unlock className="w-3 h-3 mr-1 inline" />}
                  {st}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="command-card overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 240px)" }}>
          <table className="excel-table w-full text-[12px]">
            <thead>
              <tr>
                <th className="sticky-col text-left py-2 px-3 text-[10px] uppercase tracking-wider font-semibold min-w-[280px]">
                  KPI Name
                </th>
                {MONTHS.map((m) => (
                  <th key={m} className="text-center py-2 px-2 text-[10px] uppercase tracking-wider font-semibold min-w-[64px]">
                    {m}
                  </th>
                ))}
                <th
                  className="text-center py-2 px-2 text-[10px] uppercase tracking-wider font-semibold min-w-[80px]"
                  style={{ background: "#FFB517", color: "#183C55" }}
                >
                  YTD
                </th>
                <th
                  className="text-center py-2 px-2 text-[10px] uppercase tracking-wider font-semibold min-w-[100px]"
                  style={{ background: "#54A57B", color: "white" }}
                >
                  Consolidate
                </th>
              </tr>
            </thead>

            <tbody>
              {visibleRows.map((row, ri) => (
                <tr key={row.key} className={ri % 2 ? "bg-slate-50/60" : ""}>
                  <td className="sticky-col py-1.5 px-3 font-medium text-[#0E2336] border-r border-slate-100 whitespace-nowrap">
                    {row.name}
                    {row.computed && (
                      <span className="ml-2 text-[9px] uppercase tracking-wider text-[#54A57B] font-bold">
                        auto
                      </span>
                    )}
                  </td>

                  {MONTHS.map((m, mi) => {
                    const v = getVal(row, mi);
                    const bg = cellColor(row, v);

                    return (
                      <td key={mi} style={{ background: bg }} className="border border-slate-100">
                        <input
                          data-testid={`kpi-cell-${row.key}-${mi}`}
                          value={v}
                          readOnly={row.computed}
                          onChange={(e) => update(row.key, mi, e.target.value)}
                          className={row.computed ? "text-slate-400" : ""}
                        />
                      </td>
                    );
                  })}

                  <td
                    className="text-right py-1.5 px-3 font-bold tabular text-[#7A5300] border-l border-slate-200"
                    style={{ background: "#FFF8E6" }}
                  >
                    {ytd(row).toLocaleString()}
                  </td>

                  <td
                    className="text-right py-1.5 px-3 font-bold tabular text-[#1F5F3D]"
                    style={{ background: "#ECFAF2" }}
                  >
                    {consolidate(row).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Formula label="TRIR" formula="(FAC + MTC + RWC + LTI) × 200,000 / Manhours" />
        <Formula label="LTIFR" formula="LTI × 1,000,000 / Manhours" />
        <Formula label="Severity Rate" formula="Lost Days × 200,000 / Manhours" />
        <Formula label="Closure %" formula="Closed / Total × 100" />
      </div>
    </div>
  );
}

function Formula({ label, formula }) {
  return (
    <div className="command-card p-3.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="font-mono text-[12px] text-[#183C55] mt-1">{formula}</div>
    </div>
  );
}